import { useCallback, useEffect, useRef } from 'react';
import { usePopoverContext } from '@fiverr-private/popover';

import { MenuItem } from '../../Dropdown/types';
import useDropdownContext from '../../Dropdown/useDropdownContext';
import useMenuNavigation from '../../DropdownMenu/useMenuNavigation';

const useContent = () => {
  const { setOpen, open } = usePopoverContext();
  const { values, anchorRef, items, searchWhenClosed } = useDropdownContext();
  const hasOpened = useRef(open);

  const focusContent = () => {
    const currentHoveredItem = items.find((item) => !item.disabled && item.ref.current?.matches?.(':hover'));

    if (currentHoveredItem) {
      currentHoveredItem.ref.current?.focus();
      return;
    }

    const hasSelectedItems = Boolean(Object.keys(values).length);

    if (hasSelectedItems) {
      const firstSelectedItemIdx = items.findIndex((item) =>
        Array.isArray(values[item.name])
          ? (values[item.name] as string[]).includes(item.value as string)
          : values[item.name] === item.value
      );

      items[firstSelectedItemIdx]?.ref.current?.focus();
    } else {
      const firstItem = items.find((item) => !item.disabled);
      firstItem?.ref.current?.focus();
    }
  };

  const onAnimationStart = () => {
    if (open) {
      if (searchWhenClosed) {
        setTimeout(focusContent, 50);
      } else {
        focusContent();
      }
    }
  };

  useEffect(() => {
    if (open) {
      hasOpened.current = true;
    } else if (hasOpened.current) {
      hasOpened.current = false;
      anchorRef.current?.focus();
    }
  }, [open, hasOpened, anchorRef]);

  const addMenuItem = useCallback((item: MenuItem) => items.push(item), [items]);

  const removeMenuItem = useCallback(
    (item: MenuItem['ref']) => {
      const index = items.findIndex(({ ref }) => ref.current === item.current);

      if (index > -1) {
        items.splice(index, 1);
      }
    },
    [items]
  );

  const closeMenu = useCallback(() => {
    if (!open) {
      return;
    }

    const isMulti = items.some((item) => item.type === 'checkbox');

    if (!isMulti) {
      setOpen?.(false);
    }
  }, [open, items, setOpen]);

  useMenuNavigation({ items, onEnter: closeMenu, onSpace: closeMenu });

  return { closeMenu, addMenuItem, removeMenuItem, onAnimationStart };
};

export default useContent;
